import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    User, Calendar, CreditCard, DollarSign, Building2, FileText,
    Loader2, Receipt, CheckCircle2,
    ArrowLeft,
    ChevronLeft
} from 'lucide-react';

import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import SelectBox from '../components/ui/SelectBox';
import { useToast } from '../hooks/useToast';

import { 
    client, 
    fetchStores, 
    fetchTotalReceivableBalance, 
    fetchTotalNetBalance,
    fetchUtilCustomers,
    fetchAllBankAccounts,
    fuzzySearchAccounts,
    fetchStoresWhereStaffAssigned,
} from '../apiHelper';
import { useEffectEvent } from 'react';
import TrialModal from '../components/ui/TrialModal';

const ReceivePayment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const incoming = location.state || {};

    const getInitialFormState = useCallback(() => {
        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        const localISOTime = new Date(now - offset).toISOString().slice(0, 16);
        return {
            creditsale_id: incoming.creditsale_id || '',
            orders_id: incoming.orders_id || '',
            customer_id: incoming.customer_id || '',
            invoice_number: incoming.invoice_number || '',
            store_id: incoming.store_id || '',
            currency: incoming.currency || 'NGN',
            amount_received: '',
            bank_charges: '0.00',
            tax_deducted: 'no',
            tax_withheld_amount: '0.00',
            tax_account_id: '',
            payment_date: localISOTime,
            payment_mode: 'bank transfer',
            deposit_to_account_id: '',
            reference: '',
        };
    }, [incoming])

    const [user, setUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [storeOptions, setStoreOptions] = useState([]);

    const [formData, setFormData] = useState(getInitialFormState());

    // Options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]); // ← Renamed for clarity
    const [taxAccountOptions, setTaxAccountOptions] = useState([]);   // ← Will fix later

    // Loading states
    const [loadingUser, setLoadingUser] = useState(true);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
    const [loadingTaxAccounts, setLoadingTaxAccounts] = useState(true);
    const [loadingStores, setLoadingStores] = useState(true);
    const [totalReceivableBalance, setTotalReceivableBalance] = useState(null);
    const [loadingBalance, setLoadingBalance] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    const fetchCustomerBalance = useCallback(async () => {
        if (!formData.customer_id || !formData.payment_date) {
            setTotalReceivableBalance(null);
            return;
        }

        setLoadingBalance(true);
        try {
            const payload = {
            // const response = await fetchTotalNetBalance({
                customer_id: Number(formData.customer_id),
                as_of_date: new Date().toLocaleString('sv-SE').replace(' ', 'T').slice(0, 16),
                // as_of_date: formData.payment_date
            }

            // console.log(payload)

            const response = await fetchTotalReceivableBalance(payload);
            // Handle case where API returns just a plain number (as seen in console: 32000)
            let numericBalance = 0;
            if (typeof response === 'number') {
                numericBalance = response;
            } else if (response && typeof response === 'object') {
                // Fallback to common keys if it's an object
                numericBalance =
                    response.total_receivable ??
                    response.balance ??
                    response.amount ??
                    response.receivable ??
                    0;
            }

            numericBalance = parseFloat(numericBalance) || 0;

            // console.log('Final set balance:', numericBalance); // Temporary debug

            setTotalReceivableBalance(numericBalance);
        } catch (err) {
            console.error('Failed to fetch receivable balance:', err);
            setTotalReceivableBalance(null);
            // Optionally show toast
            showToast('Could not load customer balance', 'warning');
        } finally {
            setLoadingBalance(false);
        }
    }, [formData.customer_id, formData.payment_date]);

    // Load user
    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to receive payments.', 'info');
            navigate('/login');
        }
        setLoadingUser(false);
    }, [showToast, navigate]);

    // Fetch stores (Branches)
    useEffect(() => {
        if (!user) return;
        const loadStores = async () => {
            setLoadingStores(true);
            try {
                // const stores = await fetchStores(user);
                const stores = await fetchStoresWhereStaffAssigned(user);
                setStoreOptions(stores.map(store => ({
                    label: store.identifier || `Branch ${store.store_id}`,
                    value: store.store_id,
                    storeData: store
                })));
            } catch (err) {
                showToast('Failed to load branches', 'error');
                setStoreOptions([]);
            } finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast]);

    // Fetch Customers
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user) return;
        setLoadingCustomers(true);
        try {
            const customers = await fetchUtilCustomers(user, searchQuery);
            const opts = customers.map(c => {
                const u = c.user || {};
                const fullName = `${(u.first_name || '').trim()} ${(u.middle_name || '').trim()} ${(u.last_name || '').trim()}`.trim() || 'Unnamed Customer';
                const logonId = u.credential?.logon_id || 'No ID';
                return {
                    label: `${fullName} (${logonId})`,
                    value: c.member_id,
                    customerData: c
                };
            });
            setCustomerOptions(opts);

            if (!formData.customer_id && incoming.customer_id) {
                const match = opts.find(o => o.value == incoming.customer_id);
                if (match) {
                    setFormData(prev => ({ ...prev, customer_id: match.value }));
                }
            }
        } catch (err) {
            showToast('Failed to load customers', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, incoming.customer_id, formData.customer_id, showToast]);

    useEffect(() => {
        if (user) fetchCustomers();
    }, [user, fetchCustomers]);

    // Fetch Bank/Cash Accounts — EXACTLY like your working example
    useEffect(() => {
        if (!user) return;

        const loadBankAccounts = async () => {
            setLoadingBankAccounts(true);
            try {
                const accounts = await fetchAllBankAccounts(); // ← No parameters

                const filteredAndSorted = accounts
                    .filter(a => a.is_active && a.is_leaf)
                    .map(a => ({
                        label: `${a.name} (${a.account_code})`,
                        value: a.account_id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setBankAccountOptions(filteredAndSorted);
            } catch (err) {
                showToast('Failed to load bank accounts', 'error');
                setBankAccountOptions([]);
            } finally {
                setLoadingBankAccounts(false);
            }
        };

        loadBankAccounts();
    }, [user, showToast]);

    // Load Tax Accounts (using fuzzySearchAccounts)
    useEffect(() => {
        if (!user) return;

        const loadTaxAccounts = async () => {
            setLoadingTaxAccounts(true);
            try {
                const accounts = await fuzzySearchAccounts('tax'); // ← Exact call as requested

                const filtered = accounts
                    .filter(a => a.is_active && a.is_leaf)
                    .map(a => ({
                        label: `${a.name} (${a.account_code})`,
                        value: a.account_id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setTaxAccountOptions(filtered);
            } catch (err) {
                showToast('Failed to load tax accounts', 'error');
                setTaxAccountOptions([]);
            } finally {
                setLoadingTaxAccounts(false);
            }
        };

        loadTaxAccounts();
    }, [user, showToast]);

    useEffect(() => {
        fetchCustomerBalance()
    }, [fetchCustomerBalance])

    // Fixed input handler
    const handleInputChange = ({ target: { name, value } }) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!formData.customer_id) return 'Customer is required.';
        if (!formData.amount_received || Number(formData.amount_received) <= 0)
            return 'Amount received must be greater than zero.';
        if (!formData.deposit_to_account_id) return 'Deposit to account is required.';
        if (!formData.payment_date) return 'Payment date is required.';

        if (formData.tax_deducted === 'yes') {
            if (!formData.tax_withheld_amount || Number(formData.tax_withheld_amount) <= 0)
                return 'Tax withheld amount is required.';
            if (!formData.tax_account_id)
                return 'Tax liability account is required when tax is deducted.';
        }
        return null;
    };

    const handleSubmit = async () => {
        const error = validateForm();
        if (error) {
            showToast(error, 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                amount_received: parseFloat(formData.amount_received),
                bank_charges: parseFloat(formData.bank_charges || 0),
                tax_withheld_amount: formData.tax_deducted === 'yes' ? parseFloat(formData.tax_withheld_amount) : 0,
                tax_account_id: formData.tax_deducted === 'yes' ? Number(formData.tax_account_id) : null,
                deposit_to_account_id: Number(formData.deposit_to_account_id),
                customer_id: Number(formData.customer_id),
                creditsale_id: formData.creditsale_id ? Number(formData.creditsale_id) : null,
                orders_id: formData.orders_id ? Number(formData.orders_id) : null,
                store_id: formData.store_id ? Number(formData.store_id) : null,
                received_by: user.users_id,
                receivable_balance: totalReceivableBalance
            };

            // console.log('Recording payment →', payload);

            let response;
            if (payload.orders_id) {
                response = await client.postData('/sales/receive-payment-against-invoice', payload);
            } else {
                response = await client.postData('/sales/receive-payment', payload);
            }
            showToast(response.msg || 'Payment recorded successfully!', 'success');
            // clear form instead of navigating
            setFormData(getInitialFormState())
            // navigate('/sales/payments');

            // re-fetch balance to show updated amount
            fetchCustomerBalance();
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.msg || 'Failed to record payment.';
            showToast(msg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loadingUser) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="animate-spin h-10 w-10 text-gmail-blue" />
            </div>
        );
    }

    return (
        <div className="w-full mx-auto p-4 md:p-6 max-w-5xl">
            <div className="mb-8">
                <div className="flex items-center gap-4 mb-2">
                    <Button onClick={() => navigate(-1)}
                        variant="indigolight">
                        <div className="flex items-center justify-between">
                            <ChevronLeft size={24} className="text-text-secondary" />
                            Go Back
                        </div>
                    </Button>
                    <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                        <Receipt className="text-gmail-blue" />
                        Receive Payment
                    </h1>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <p className="text-text-secondary text-sm">
                            {incoming.invoice_number && `#(${incoming.invoice_number})`}
                        </p>

                        {formData.customer_id && (
                            <div className="text-right">
                                <p className="text-xs text-gray-500">Total Receivable Balance</p>
                                <p className="text-xl font-bold text-gmail-blue">
                                    {loadingBalance ? (
                                        <Loader2 className="inline animate-spin h-5 w-5" />
                                    ) : totalReceivableBalance !== null ? (
                                        `₦${totalReceivableBalance.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}`
                                    ) : (
                                        '—'
                                    )}
                                </p>
                                <p className="text-xs text-gray-400">
                                    as of {new Date(formData.payment_date).toLocaleString([], {
                                        dateStyle: 'medium',
                                        timeStyle: 'short'
                                    })}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Card className="p-6 space-y-8">
                {/* Customer */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                        Customer <span className="text-gmail-red">*</span>
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <SearchableSelectBox
                            name="customer_id"
                            value={formData.customer_id}
                            onChange={handleInputChange}
                            options={customerOptions}
                            onSearch={fetchCustomers}
                            loading={loadingCustomers}
                            placeholder="Search customer..."
                            required
                            isDisabled={!!incoming.customer_id}
                        />
                    </div>
                </div>

                {/* Amount & Charges */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SearchableSelectBox
                        name="store_id"
                        label="Branch"
                        required
                        value={formData.store_id}
                        onChange={handleInputChange}
                        options={storeOptions}
                        loading={loadingStores}
                        placeholder="Select branch..."
                        noOptionsMessage={() => "No branches found"}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        icon={<Building2 size={18} className="text-gray-500" />}
                    />

                    <Input
                        type="number"
                        step="0.01"
                        name="amount_received"
                        label="Amount Received"
                        placeholder="0.00"
                        value={formData.amount_received}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        type="number"
                        step="0.01"
                        name="bank_charges"
                        label="Bank Charges (if any)"
                        placeholder="0.00"
                        value={formData.bank_charges}
                        onChange={handleInputChange}
                    />
                </div>

                {/* Tax Deducted */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                        Tax Deducted at Source (TDS/WHT)?
                    </label>
                    <div className="flex items-center gap-8">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="tax_deducted"
                                value="no"
                                checked={formData.tax_deducted === 'no'}
                                onChange={handleInputChange}
                                className="text-gmail-blue focus:ring-gmail-blue"
                            />
                            <span>No</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="tax_deducted"
                                value="yes"
                                checked={formData.tax_deducted === 'yes'}
                                onChange={handleInputChange}
                                className="text-gmail-blue focus:ring-gmail-blue"
                            />
                            <span>Yes</span>
                        </label>
                    </div>

                    {formData.tax_deducted === 'yes' && (
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                                type="number"
                                step="0.01"
                                name="tax_withheld_amount"
                                label="Amount Withheld (Tax)"
                                placeholder="0.00"
                                value={formData.tax_withheld_amount}
                                onChange={handleInputChange}
                                required
                            />
                            <SearchableSelectBox
                                name="tax_account_id"
                                label="Tax Account"
                                value={formData.tax_account_id}
                                onChange={handleInputChange}
                                options={taxAccountOptions}
                                loading={loadingBankAccounts} // reuse same loader for now
                                placeholder="Select tax account..."
                                required
                            />
                        </div>
                    )}
                </div>

                {/* Payment Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                        type="datetime-local"
                        name="payment_date"
                        label="Payment Date & Time"
                        value={formData.payment_date}
                        onChange={handleInputChange}
                        required
                        // disabled={true}
                    />
                    <SelectBox
                        name="payment_mode"
                        label="Payment Mode"
                        value={formData.payment_mode}
                        onChange={handleInputChange}
                        options={[
                            { value: 'cash', label: 'Cash' },
                            { value: 'bank_transfer', label: 'Bank Transfer' },
                            { value: 'card', label: 'Card / POS' },
                        ]}
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SearchableSelectBox
                        name="deposit_to_account_id"
                        label="Deposit To (Bank/Cash Account)"
                        value={formData.deposit_to_account_id}
                        onChange={handleInputChange}
                        options={bankAccountOptions}
                        loading={loadingBankAccounts}
                        placeholder="Select bank account..."
                        required
                    />
                    <Input
                        name="reference"
                        label="Reference # (Optional)"
                        placeholder="e.g. TRF123456, Cheque #001"
                        value={formData.reference}
                        onChange={handleInputChange}
                    />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                    <Button variant="ghost" onClick={() => navigate(-1)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="animate-spin" size={18} />
                                Recording...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 size={18} />
                                Record Payment
                            </>
                        )}
                    </Button>
                </div>
            </Card>
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default ReceivePayment;